import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Smartphone, Loader2, ShieldCheck, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import paymentAPI from './paymentApi';

const POLL_INTERVAL_MS = 4000;

// M-Pesa payments go through Pesapal's hosted checkout, embedded here as an
// iframe so the customer never leaves the app (Pesapal officially supports
// this - see developer.pesapal.com). Because the final redirect back to our
// callback URL happens *inside* the iframe (and doesn't always reliably
// propagate visually), we don't rely on watching the iframe navigate at all
// - instead we poll our own backend's status endpoint in the background,
// which itself asks Pesapal for the real status. That's the reliable path
// regardless of what the iframe is doing.
const MpesaModal = ({ isOpen, onClose, orderId, amount, userEmail }) => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [orderTrackingId, setOrderTrackingId] = useState(null);
  const [finalStatus, setFinalStatus] = useState(null); // 'completed' | 'failed' | null
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => stopPolling, []); // cleanup on unmount

  const startPolling = (trackingId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const result = await paymentAPI.getMpesaStatus(trackingId);
        if (result.status === 'completed') {
          stopPolling();
          setFinalStatus('completed');
        } else if (result.status === 'failed') {
          stopPolling();
          setFinalStatus('failed');
        }
        // still 'pending' -> keep polling silently
      } catch (err) {
        // transient network hiccup - just try again next tick
        console.warn('Payment status check failed, will retry:', err);
      }
    }, POLL_INTERVAL_MS);
  };

  const formatPhoneNumber = (value) => {
    let cleaned = value.replace(/\D/g, '');
    if (cleaned.startsWith('254')) return cleaned;
    if (cleaned.startsWith('0')) return '254' + cleaned.slice(1);
    if (cleaned.startsWith('7') || cleaned.startsWith('1')) return '254' + cleaned;
    return cleaned;
  };

  const handlePhoneChange = (e) => {
    const cleaned = e.target.value.replace(/\D/g, '').slice(0, 12);
    setPhoneNumber(cleaned);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formattedPhone = formatPhoneNumber(phoneNumber);

    if (formattedPhone.length !== 12) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await paymentAPI.initiateMpesa(formattedPhone, amount, orderId, userEmail);
      if (response.success && response.redirect_url) {
        setCheckoutUrl(response.redirect_url);
        setOrderTrackingId(response.order_tracking_id);
        startPolling(response.order_tracking_id);
      } else {
        toast.error('Failed to start payment');
      }
    } catch (error) {
      console.error('Pesapal error:', error);
      toast.error(error.response?.data?.detail || 'Payment initiation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    stopPolling();
    setPhoneNumber('');
    setCheckoutUrl(null);
    setOrderTrackingId(null);
    setFinalStatus(null);
    onClose();
  };

  const handleViewOrder = () => {
    handleClose();
    navigate(`/payment/callback?status=${finalStatus}&order_id=${orderId}`);
  };

  // ---- Result screen (payment finished, detected via polling) ----
  if (finalStatus) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-6 space-y-4">
            {finalStatus === 'completed' ? (
              <>
                <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto" />
                <h3 className="text-xl font-bold">Payment Successful!</h3>
                <p className="text-muted-foreground">Your M-Pesa payment has been confirmed.</p>
              </>
            ) : (
              <>
                <XCircle className="w-16 h-16 text-red-600 mx-auto" />
                <h3 className="text-xl font-bold">Payment Not Completed</h3>
                <p className="text-muted-foreground">Your payment didn't go through. No charge was made.</p>
              </>
            )}
            <Button onClick={handleViewOrder} className="w-full">Continue</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ---- Embedded Pesapal checkout ----
  if (checkoutUrl) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-2xl p-0 gap-0 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <button
              onClick={() => { stopPolling(); setCheckoutUrl(null); }}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <span className="text-sm font-medium flex items-center gap-1">
              <ShieldCheck className="w-4 h-4 text-green-600" /> Secured by Pesapal
            </span>
          </div>
          <iframe
            src={checkoutUrl}
            title="Pesapal Checkout"
            className="w-full"
            style={{ height: '620px', border: 'none' }}
          />
          <div className="px-4 py-2 text-xs text-center text-muted-foreground border-t bg-muted/30">
            Waiting for payment confirmation... this page will update automatically once you're done.
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ---- Phone number entry ----
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-green-600" />
            M-Pesa Payment
          </DialogTitle>
          <DialogDescription>
            Pay KES {amount?.toLocaleString()} securely with M-Pesa
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="phone">M-Pesa Phone Number</Label>
            <div className="flex mt-2">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">
                +254
              </span>
              <Input
                id="phone"
                type="tel"
                placeholder="7XX XXX XXX"
                value={phoneNumber.startsWith('254') ? phoneNumber.slice(3) : phoneNumber}
                onChange={handlePhoneChange}
                className="rounded-l-none"
                maxLength={9}
                required
              />
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-green-800">Amount to Pay</span>
              <span className="text-xl font-bold text-green-800">
                KES {amount?.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="w-4 h-4 shrink-0" />
            <span>You'll complete payment right here - we never see your M-Pesa PIN.</span>
          </div>

          <Button type="submit" className="w-full bg-green-600 hover:bg-green-700" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Preparing checkout...
              </>
            ) : (
              'Continue to Pay'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MpesaModal;
